import React, { useState, useCallback } from 'react';
import ScrollReveal from './ScrollReveal';
import { trackFAQToggle } from '../utils/analytics';

const faqs = [
  {
    q: 'Do I need to install new cameras?',
    a: 'No. Oscilabs Vision connects to your existing camera infrastructure. The edge AI device ingests feeds from any IP or analog camera system already on site.',
  },
  {
    q: 'Does it work with unreliable power and internet?',
    a: 'Yes. The system is designed for intermittent power and connectivity environments. The edge device processes video locally, stores data during outages, and auto-syncs when connection resumes. It is solar and generator compatible, and works over satellite and 3G.',
  },
  {
    q: 'Is raw video sent to the cloud?',
    a: 'No. All video processing happens locally on the edge device. Only lightweight structured data \u2014 KPIs, metrics, and aggregated analytics \u2014 is transmitted via encrypted connection. Raw video never leaves the facility.',
  },
  {
    q: 'Does the system use facial recognition?',
    a: 'No. Oscilabs Vision performs analytics-only processing. It measures activity, occupancy, and movement patterns using aggregated, non-identifying metrics. No facial recognition, no identity storage, no personal identification. All metrics are time-stamped and aggregated with privacy at the core.',
  },
  {
    q: 'What kind of facilities is this designed for?',
    a: 'Oscilabs Vision is deployed in hospitals, clinics, schools, distribution centers, and government facilities. It is particularly suited for humanitarian and development contexts in resource-constrained environments.',
  },
  {
    q: 'How long does deployment take?',
    a: 'A single-site deployment typically takes less than one day. The edge device connects to existing cameras, begins processing immediately, and dashboards are available within hours. Remote activation is supported.',
  },
];

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div className={`faq-item${isOpen ? ' open' : ''}`}>
      <button className="faq-question" onClick={onToggle} aria-expanded={isOpen}>
        <span>{faq.q}</span>
        <span className="faq-icon">{isOpen ? '\u2212' : '\u002B'}</span>
      </button>
      <div className="faq-answer" style={{ maxHeight: isOpen ? 300 : 0 }}>
        <p>{faq.a}</p>
      </div>
    </div>
  );
}

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);

  const toggle = useCallback((i) => {
    setOpenIndex((prev) => {
      const isOpening = prev !== i;
      if (isOpening) trackFAQToggle(faqs[i].q);
      return prev === i ? -1 : i;
    });
  }, []);

  return (
    <section className="section faq-section" id="faq">
      <div className="section-inner" style={{ maxWidth: 820, margin: '0 auto' }}>
        <div className="section-label">Frequently Asked Questions</div>
        <ScrollReveal>
          <h2 className="section-title">
            Common questions, <strong>clear answers</strong>
          </h2>
        </ScrollReveal>

        <ScrollReveal>
          <div className="faq-list">
            {faqs.map((faq, i) => (
              <FAQItem key={i} faq={faq} isOpen={openIndex === i} onToggle={() => toggle(i)} />
            ))}
          </div>
        </ScrollReveal>

        <ScrollReveal>
          <div className="faq-cta-strip">
            <p>Have a question not covered here?</p>
            <a
              href="#contact"
              className="btn-primary"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Talk to Our Team
            </a>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}
